import {
  ElMessageBox,
  ElNotification,
  NotificationHandle,
  dateEquals,
  useDialog,
} from "element-plus";
import { C, UA, WebSocketInterface } from "jssip";
import * as Sentry from "@sentry/vue";
import { useAgentSipConfigStore } from "~/stores/agentSipConfigStore";
import { postRequest } from "~/apis/requests/useApiCall";
import { User } from "~/types";
import { InCall } from "~/types/enums/InCall";
import { LiveUpdatePayload } from "~/types/liveUpdates";
import { CallOrigin } from "~/types/enums/callOrigin";
import { useConfigStore } from "~/stores/configStore";
import { useAutoDialerStore } from "~/projects/tele-sales/stores/autoDialerStore";
import {
  setupAudioStream,
  stop,
  play,
} from "~/projects/cc/services/AudioService";
import { useDialogStore } from "~/projects/cc/stores/dialogStore";
import {
  getCustomerUriInfo,
  getCleanedCustomerNumber,
} from "~/projects/cc/utils/callCenterHelper";
const customerProfileStore = useCustomerProfileStore();
/**
 * Handle Call Related Functionalities
 */
export default class CallService {
  public sipConfig: {
    password: any;
    sockets: WebSocketInterface[];
    uri: string;
    register: boolean;
  };

  private readonly userAgent;
  private readonly options;
  private readonly store: any;

  private randomId = "";
  private initiatingOutgoingCall: boolean;
  private callSessionStartTime: number;
  private session: any;
  private phone: any;
  private event: any;

  private connectionNotification: NotificationHandle | null;

  constructor() {
    this.store = useAgentSipConfigStore();
    this.sipConfig = this.getSipConfiguration();
    this.userAgent = new UA(this.sipConfig);
    this.initiatingOutgoingCall = false;
    this.callSessionStartTime = 0;
    this.phone = null;
    this.session = null;
    this.options = {
      mediaConstraints: {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      },
    };
    this.event = useNuxtApp().$event;
    this.connectionNotification = null;
  }

  public endConnection(): void {
    if (this.phone) {
      this.phone.off("connected", this.connectedHandler);
      this.phone.off("disconnected", this.disconnectedHandler);
      this.phone.off("registered", this.registeredHandler);
      this.phone.off("unregistered", this.unregisteredHandler);
      this.phone.off("registrationFailed", this.registrationFailedHandler);
      this.phone.off("newRTCSession", this.rtcSessionHandler);
      this.phone.off("newMessage", this.newMessageHandler);
      this.phone.stop();
      console.log("stopping wss connection");
    }
    this.phone = null;
  }

  /**
   * @description initialize of Call Configuration
   */
  public async init(mustConnect = false) {
    this.store.setDisconnected();
    this.randomId = Math.random().toString(36).substring(7);
    if (!mustConnect) {
      // wait for prev network conns to release from asterisk side
      // helps when browser is just refrehsed
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const othersAlreadyConnected =
        await this.store.isOtherAsteriskSessionsConnected();
      if (othersAlreadyConnected) {
        this.store.setConnectedElsewhere();
        ElNotification({
          type: "warning",
          title: "Other Session Connected",
          message: "You are connected in other session. ",
          duration: 3000,
          showClose: true,
        });
        return;
      }
    }
    const disconnectResponse =
      await this.store.requestOtherAsteriskSessionsDisconnect(this.randomId);
    if (disconnectResponse !== true) {
      console.log("Failed to disconnect other asterisk sessions");
      // TODO: maybe show notif that force disconnect attempt failed
    }
    this.resetCallSession();
    if (localStorage.getItem("voice_access") === null) {
      localStorage.setItem("voice_access", "prompt");
    }
    this.phone = this.userAgent;
    this.phone.on("connected", this.connectedHandler);
    this.phone.on("disconnected", this.disconnectedHandler);
    this.phone.on("registered", this.registeredHandler);
    this.phone.on("unregistered", this.unregisteredHandler);
    this.phone.on("registrationFailed", this.registrationFailedHandler);
    this.phone.on("newRTCSession", this.rtcSessionHandler);
    this.phone.on("newMessage", this.newMessageHandler);
    this.phone.start();
  }

  public endCall() {
    try {
      this.session.terminate();
    } catch (e) {
      this.session = null;
    }
  }

  public async makeCall(
    number: string,
    options: CallOptions = {},
    teleSale: any,
    autoDialer?: boolean,
  ) {
    if (autoDialer) {
      useAutoDialerStore().autoDialer = true;
    } else {
      useAutoDialerStore().autoDialer = false;
    }

    if (
      useDialogStore().teleSaleInfo?.teleSale?.id &&
      useDialogStore().teleSaleInfo?.number === number
    ) {
      teleSale.id = useDialogStore().teleSaleInfo?.teleSale?.id;
      options = { origin: CallOrigin.TeleSales };
    }

    if (teleSale?.id) {
      useDialogStore().setTeleSaleInfo({ teleSale, number });
    }

    if (this.phone === null || this.store.connectionStatus !== "connected") {
      if (this.store.connectionStatus === "connected_elsewhere") {
        const r = await ElMessageBox.prompt(
          "Telephony is not active, possibly connected in an another session. Connect now and call?",
          { title: "Force call?" },
        );
        if (r.action !== "confirm") return;
        await this.init(true);
      } else {
        ElMessageBox({
          type: "error",
          title: "Telephony not active",
          message:
            "Your telephony connection is not active, please contact admin or try again",
        });
        return;
      }
    }
    if (this.session !== null || this.initiatingOutgoingCall) {
      console.log("already in call, cannot place another call");
      return;
    }

    this.initiatingOutgoingCall = true;
    const { response, error } = await useDialogStore().initiateCall(
      number,
      options,
      teleSale?.id,
    );
    if (!error) {
      const history = response.data;
      this.placeSipCall(number + ";" + history.id);
      useDialogStore().setCurrentCallHistoryId(history.id);
      useDialogStore().setLastCallAgent(history.prev_agent ?? null);
      const incomingCallNumber = ref("");
      incomingCallNumber.value = getCleanedCustomerNumber(number);
      const profile = await customerProfileStore.getProfileByPhone(
        incomingCallNumber.value,
      );
      console.log("profile", profile);
      if (profile) useDialogStore().setCurrentProfile(profile);
    } else {
      console.log("call ini api call error", error);
      this.initiatingOutgoingCall = false;
      ElMessageBox({
        type: "error",
        title: "Call failed",
        message:
          "Call rejected by system: " +
          (error.response?.data?.message || error.toString()),
      });
    }
  }

  private placeSipCall(uri: string) {
    this.resetCallSession();
    console.log("call initiation started");
    this.phone.call("sip:" + uri, this.options);
  }

  public acceptCall() {
    this.session.answer(this.options);
  }

  public async transferCall(agent: User) {
    const { callHistoryId } = getCustomerUriInfo(this.getCallerUri());
    return await postRequest("cc/call-transfer/transfer-to-agent", {
      ...agent,
      call_history_id: callHistoryId,
    });
  }

  public toggleMute = () => {
    console.log("session", this.session);
    if (this.session.isMuted().audio) {
      this.session.unmute({ audio: true });
      return false;
    } else {
      this.session.mute({ audio: true });
      return true;
    }
  };

  public togglePause = () => {
    if (this.session.isOnHold().local) {
      this.session.unhold({ audio: true });
      return false;
    } else {
      this.session.hold({ audio: true });
      return true;
    }
  };

  public sendDtmf = (tone: string) => {
    if (this.session === null) {
      console.log("no active session, cannot send dtmf");
      return false;
    }
    try {
      this.session.sendDTMF(tone);
      return true;
    } catch (e) {
      console.log("failed to send dtmf", e);
      return false;
    }
  };

  public progressFunction = () => {
    console.log("call is in progress");
    stop("trying");
    const evt = { direction: this.session.direction };
    this.event("progress", evt);
  };

  public acceptedFunction = () => {
    console.log("Call is accepted");
    play("answered");
    this.event("accepted");
  };

  public failedSession = (e: any) => {
    console.log("call session is failed");
    console.log("cause: " + e.cause);
    this.resetCallSession();
    stop("trying");
    stop("incomingCall");
    const rejectedCauses = [C.causes.REJECTED, C.causes.CANCELED];
    const missedCauses = [
      C.causes.REQUEST_TIMEOUT,
      C.causes.NO_ANSWER,
      C.causes.BUSY,
    ];
    if (rejectedCauses.includes(e.cause)) {
      play("rejected");
      useDialogStore().setInCall(InCall.REJECTED);
    } else if (missedCauses.includes(e.cause)) {
      play("missed");
      useDialogStore().setInCall(InCall.MISSED);
    } else {
      play("failed");
      useDialogStore().setInCall(InCall.FAILED);
    }
    this.event("completed");
  };

  public completeSession = () => {
    console.log("call session is completed");
    this.resetCallSession();
    this.event("completed");
  };

  public confirmedCallback = () => {
    console.log("call is confirmed");
    stop("trying");
    this.event("confirmed");
    console.log("confirmed call back");
  };

  public incomingCallStream = (peerconnection: any) => {
    console.log("called incoming call stream");
    peerconnection.onaddstream = function (e: any) {
      stop("incomingCall");
      console.log("I am calling for incoming call");
      setupAudioStream(e.stream);
    };
  };

  public outgoingCallStream = (session: any) => {
    console.log("called outgoing call stream");

    session.connection.addEventListener("addstream", function (e: any) {
      console.log("I am calling for outgoing call");
      setupAudioStream(e.stream);
    });
  };

  public rtcSessionHandler = (data: any) => {
    console.log("rtc session handler called");
    if (this.session != null) {
      console.log(
        `warning ${data.session.direction} call during existing ${this.session.direction} call`,
      );
      Sentry.captureMessage("call in another call", {
        extra: {
          oldDirection: this.session.direction,
          oldDuration: Math.floor(
            (Date.now() - this.callSessionStartTime) / 1000,
          ),
          newDirection: data.session.direction,
        },
      });
    }
    this.initiatingOutgoingCall = false;
    this.callSessionStartTime = Date.now();
    this.session = data.session;
    this.session.on("progress", this.progressFunction);
    this.session.on("ended", this.completeSession);
    this.session.on("failed", this.failedSession);
    this.session.on("accepted", this.acceptedFunction);
    this.session.on("confirmed", this.confirmedCallback);
    this.session.on("getusermediafailed", (err: any) => {
      console.log(err);
      ElMessageBox({
        type: "error",
        title: "Error",
        message: "Microphone permission issue: " + err.message,
      });
    });
    this.session.on("peerconnection:createofferfailed", (err: any) => {
      console.log(err);
      Sentry.captureException(err, {
        contexts: { direction: this.session.direction },
      });
    });
    this.session.on("peerconnection:createanswerfailed", (err: any) => {
      console.log(err);
      Sentry.captureException(err, {
        contexts: { direction: this.session.direction },
      });
    });
    this.session.on("peerconnection", (e: any) => {
      console.log("peer connecttion called");
      this.incomingCallStream(e.peerconnection);
    });
    if (this.session.direction === "incoming") {
      console.log("session direction incoming");
      play("incomingCall");
      this.event("incoming");
    } else {
      play("trying");
      console.log("session direction outgoing");
      this.outgoingCallStream(this.session);
      this.event("outgoing");
    }
  };

  public getCallerUri = () => {
    try {
      return this.session.remote_identity.uri.user;
    } catch (e) {
      return "NA";
    }
  };

  public getCallerData = () => {
    return this.session.remote_identity;
  };

  private resetCallSession() {
    this.session = null;
  }

  private connectedHandler = () => {
    console.log("sip ua event: connected");
  };

  private disconnectedHandler = () => {
    console.log("sip ua event: disconnected");
    this.store.setConnectionFailed();
  };

  private registeredHandler = () => {
    console.log("sip ua event: registered");
    this.store.setConnectionOk();
  };

  private unregisteredHandler = () => {
    console.log("sip ua event: unregistered");
    this.store.setConnectionFailed();
  };

  private registrationFailedHandler = () => {
    console.log("sip ua event: registrationFailed");
    this.store.setConnectionFailed();
  };

  private showNotConnectedNotification = () => {
    if (!this.connectionNotification) {
      this.connectionNotification = ElNotification({
        type: "error",
        title: "Not Connected",
        message: "You are not connected to the sip server",
        showClose: true,
        onClose: () => {
          this.connectionNotification = null;
        },
      });
    }
  };

  private hideNotConnectedNotification = () => {
    if (this.connectionNotification) {
      this.connectionNotification.close();
      this.connectionNotification = null;
    }
  };

  private getSipConfiguration = () => {
    const agentConfig = useConfigStore().globalConfig.sip_info;
    const url =
      agentConfig.sip_transport + "://" + agentConfig.sip_domain + "/ws";
    const socket = new WebSocketInterface(url);

    return {
      sockets: [socket],
      uri: agentConfig.sip_username + "@" + agentConfig.sip_domain,
      password: agentConfig.sip_password,
      register: true,
    };
  };

  public newMessageHandler = (e: any) => {
    const { originator, message, request } = e;
    if (originator !== "remote") return;

    const sender = message.remote_identity.uri.user;
    console.log("sip msg received from sender: " + sender);
    if (sender !== "system") {
      console.log("rejecting msg bcz from " + sender);
      message.reject();
      return;
    }

    message.accept();
    const payload = JSON.parse(request.body) as LiveUpdatePayload;

    const { type, data } = payload;
    console.log(type, data);

    switch (type) {
      case "notification":
        const notif = ElNotification({
          type: data.type,
          title: data.title || "Notification",
          message: data.content,
          duration: -1,
          showClose: true,
          onClick: () => {
            notif.close();
          },
        });
        this.event("notification", data);
        break;
      case "agent_message":
        this.event("agent_message", data);
        break;
      case "command":
        if (data.name === "logout") {
          if (
            data.params.requesterRandomId &&
            data.params.requesterRandomId === useAuthStore().randomId
          ) {
            break;
          }
          this.endConnection();
          useAuthStore().$reset();
          useRouter().push("/");
          const notif = ElNotification({
            type: "warning",
            title: "Logged Out",
            message: "You have been logged out by system",
            duration: -1,
            showClose: true,
            onClick: () => {
              notif.close();
            },
          });
        } else if (data.name === "reload") {
          window.location.reload();
        } else if (data.name === "logout_reload") {
          if (
            data.params.requesterRandomId &&
            data.params.requesterRandomId === useAuthStore().randomId
          ) {
            break;
          }
          this.endConnection();
          useAuthStore().$reset();
          useRouter().push("/");
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else if (data.name === "disconnect_call_service") {
          if (
            data.params.requesterRandomId &&
            data.params.requesterRandomId === this.randomId
          ) {
            break;
          }
          this.endConnection();
          if (data.params.requesterRandomId) {
            this.store.setDisconnectedByOther();
          } else {
            this.store.setDisconnected();
          }
          ElNotification({
            type: "warning",
            title: "Disconnected",
            message: "You have been disconnected by other session",
            duration: 3000,
            showClose: true,
          });
        }
        this.event("command", data);
        break;
      default:
        console.log("unknown message type");
    }
  };
}

type CallOptions = {
  origin?: CallOrigin;
};
